import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  FileText,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Table,
  Settings,
  Save,
  X,
  Database,
  MapPin,
  Plus
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ParsedColumn {
  index: number;
  name: string;
  sampleValues: string[];
  detectedType: 'text' | 'number' | 'date' | 'mixed';
}

interface FieldMapping {
  csvColumn: string;
  targetField: string;
  isRequired: boolean;
}

interface MetadataMapping {
  sampleDataKey: string;
  targetField: string;
  isRequired: boolean;
}

interface ExpectedField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date';
  required: boolean;
  description?: string;
}

interface ExpectedMetadataField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date';
  required: boolean;
  description?: string;
}

interface ParseMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: File;
  onSave: (config: any) => void;
}

const ParseMapModal = ({ isOpen, onClose, file, onSave }: ParseMapModalProps) => {
  const [parsedColumns, setParsedColumns] = useState<ParsedColumn[]>([]);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [metadataMappings, setMetadataMappings] = useState<MetadataMapping[]>([]);
  const [customMetadataFields, setCustomMetadataFields] = useState<ExpectedMetadataField[]>([]);
  const [configName, setConfigName] = useState('');
  const [configDescription, setConfigDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [parseError, setParsError] = useState('');
  const [hasHeader, setHasHeader] = useState(true);
  const [delimiter, setDelimiter] = useState(',');
  const [headerRowLabel, setHeaderRowLabel] = useState('Name'); // Default to 'Name' as it's common
  const [sampleData, setSampleData] = useState<{[key: string]: string}>({});
  const [dateFormat, setDateFormat] = useState<string>('DD/MM/YYYY');
  const [timeFormat, setTimeFormat] = useState<string>('HH:mm:ss');
  const { toast } = useToast();

  const DATE_FORMATS = [
    { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
    { label: 'DD/MM/YY', value: 'DD/MM/YY' },
    { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
    { label: 'MM/DD/YY', value: 'MM/DD/YY' },
  ];
  const TIME_FORMATS = [
    { label: '24-hour (HH:mm:ss)', value: 'HH:mm:ss' },
    { label: '12-hour (hh:mm:ss AM/PM)', value: 'hh:mm:ss A' },
  ];

  // Expected fields based on the marker positions and nominal info structure
  const expectedFields: ExpectedField[] = [
    { key: 'feature_label', label: 'Feature Label', type: 'text', required: true, description: 'Unique identifier for each feature' },
    { key: 'characteristic', label: 'Characteristic', type: 'text', required: true, description: 'Measurement characteristic' },
    { key: 'actual_value', label: 'Actual Value', type: 'number', required: false, description: 'Measured actual value' },
    { key: 'deviation', label: 'Deviation', type: 'number', required: false, description: 'Difference from nominal' }
  ];

  // Expected metadata fields - required ones
  const requiredMetadataFields: ExpectedMetadataField[] = [
    { key: 'part_number', label: 'Part Number', type: 'text', required: true, description: 'Part identification number' },
    { key: 'sample_id', label: 'Sample ID', type: 'text', required: true, description: 'Sample identifier' },
    { key: 'date', label: 'Date', type: 'date', required: true, description: 'Measurement date' },
    { key: 'time', label: 'Time', type: 'text', required: true, description: 'Measurement time' }
  ];

  // All metadata fields (required + custom)
  const allMetadataFields = [...requiredMetadataFields, ...customMetadataFields];

  const detectColumnType = (values: string[]): 'text' | 'number' | 'date' | 'mixed' => {
    let numberCount = 0;
    let dateCount = 0;
    let textCount = 0;

    for (const value of values) {
      if (!value || value.trim() === '') continue;
      
      // Check if it's a number
      if (!isNaN(Number(value)) && value.trim() !== '') {
        numberCount++;
      }
      // Check if it's a date (basic check)
      else if (Date.parse(value) && (value.includes('/') || value.includes('-'))) {
        dateCount++;
      }
      else {
        textCount++;
      }
    }

    const total = numberCount + dateCount + textCount;
    if (total === 0) return 'text';

    if (numberCount / total > 0.8) return 'number';
    if (dateCount / total > 0.8) return 'date';
    if (textCount / total > 0.8) return 'text';
    return 'mixed';
  };

  const parseFile = async () => {
    setIsLoading(true);
    setParsError('');

    try {
      const content = await file.text();
      const lines = content.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        throw new Error('File appears to be empty');
      }

      // Parse all lines first
      const allRows: string[][] = [];
      for (const line of lines) {
        const row = line.split(delimiter).map(cell => cell.trim().replace(/^["']|["']$/g, ''));
        allRows.push(row);
      }

      if (allRows.length === 0) {
        throw new Error('No valid rows found in file');
      }

      let headerRowIndex = -1;
      const extractedSampleData: {[key: string]: string} = {};

      if (hasHeader) {
        // Find the row that contains the header row label in the first column
        headerRowIndex = allRows.findIndex(row => 
          row[0] && row[0].toLowerCase().trim() === headerRowLabel.toLowerCase().trim()
        );

        if (headerRowIndex === -1) {
          throw new Error(`Header row with label "${headerRowLabel}" not found in first column`);
        }

        // Extract sample data from rows before the header
        for (let i = 0; i < headerRowIndex; i++) {
          const row = allRows[i];
          if (row.length >= 2 && row[0] && row[1]) {
            // Store as key-value pairs (field label -> value)
            extractedSampleData[row[0]] = row[1];
          }
        }
        setSampleData(extractedSampleData);

        // Use the header row for column names
        const headerRow = allRows[headerRowIndex];
        const dataRows = allRows.slice(headerRowIndex + 1);

        const columnCount = headerRow.length;
        const columns: ParsedColumn[] = [];

        for (let i = 0; i < columnCount; i++) {
          const columnName = headerRow[i] || `Column ${i + 1}`;
          const sampleValues = dataRows.map(row => row[i] || '').filter(val => val);
          const detectedType = detectColumnType(sampleValues);

          columns.push({
            index: i,
            name: columnName,
            sampleValues: sampleValues.slice(0, 5), // Show first 5 sample values
            detectedType
          });
        }

        setParsedColumns(columns);
      } else {
        // No header mode - treat all rows as data
        const sampleLines = allRows.slice(0, Math.min(20, allRows.length));
        const columnCount = Math.max(...sampleLines.map(row => row.length));
        const columns: ParsedColumn[] = [];

        for (let i = 0; i < columnCount; i++) {
          const columnName = `Column ${i + 1}`;
          const sampleValues = sampleLines.map(row => row[i] || '').filter(val => val);
          const detectedType = detectColumnType(sampleValues);

          columns.push({
            index: i,
            name: columnName,
            sampleValues: sampleValues.slice(0, 5),
            detectedType
          });
        }

        setParsedColumns(columns);
        setSampleData({}); // No sample data in this mode
      }

      // Initialize field mappings
      const initialMappings: FieldMapping[] = expectedFields.map(field => ({
        csvColumn: '',
        targetField: field.key,
        isRequired: field.required
      }));

      // Initialize metadata mappings
      const initialMetadataMappings: MetadataMapping[] = allMetadataFields.map(field => ({
        sampleDataKey: '',
        targetField: field.key,
        isRequired: field.required
      }));

      // Auto-map metadata fields
      Object.keys(extractedSampleData).forEach(key => {
        const lowerKey = key.toLowerCase();
        let matchedField = '';

        if (lowerKey.includes('part') && lowerKey.includes('number')) {
          matchedField = 'part_number';
        } else if (lowerKey.includes('sample')) {
          matchedField = 'sample_id';
        } else if (lowerKey.includes('date')) {
          matchedField = 'date';
        } else if (lowerKey.includes('time')) {
          matchedField = 'time';
        }

        if (matchedField) {
          const mappingIndex = initialMetadataMappings.findIndex(m => m.targetField === matchedField);
          if (mappingIndex !== -1 && !initialMetadataMappings[mappingIndex].sampleDataKey) {
            initialMetadataMappings[mappingIndex].sampleDataKey = key;
          }
        }
      });

      setMetadataMappings(initialMetadataMappings);

      // Try to auto-match some common column names for feature data
      parsedColumns.forEach(col => {
        const lowerName = col.name.toLowerCase();
        let matchedField = '';

        if (lowerName.includes('feature') || lowerName.includes('label') || lowerName.includes('name')) {
          matchedField = 'feature_label';
        } else if (lowerName.includes('characteristic') || lowerName.includes('control')) {
          matchedField = 'characteristic';
        } else if (lowerName.includes('actual') || lowerName.includes('meas')) {
          matchedField = 'actual_value';
        } else if (lowerName.includes('deviation') || lowerName.includes('dev')) {
          matchedField = 'deviation';
        }

        if (matchedField) {
          const mappingIndex = initialMappings.findIndex(m => m.targetField === matchedField);
          if (mappingIndex !== -1 && !initialMappings[mappingIndex].csvColumn) {
            initialMappings[mappingIndex].csvColumn = col.name;
          }
        }
      });

      setFieldMappings(initialMappings);

    } catch (error) {
      console.error('Parse error:', error);
      setParsError(error instanceof Error ? error.message : 'Failed to parse file');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && file) {
      // Reset state when modal opens, but don't auto-parse
      setParsedColumns([]);
      setFieldMappings([]);
      setMetadataMappings([]);
      setCustomMetadataFields([]);
      setSampleData({});
      setParsError('');
    }
  }, [isOpen, file]);

  const updateFieldMapping = (targetField: string, csvColumn: string) => {
    const cleanColumn = csvColumn === '__none__' ? '' : csvColumn;
    
    setFieldMappings(prev => prev.map(mapping => {
      if (mapping.targetField === targetField) {
        return { ...mapping, csvColumn: cleanColumn };
      }
      
      // Handle mutual exclusion between actual_value and deviation
      if ((targetField === 'actual_value' || targetField === 'deviation') && cleanColumn !== '') {
        const otherField = targetField === 'actual_value' ? 'deviation' : 'actual_value';
        if (mapping.targetField === otherField) {
          return { ...mapping, csvColumn: '' }; // Clear the other field
        }
      }
      
      return mapping;
    }));
  };

  const updateMetadataMapping = (targetField: string, sampleDataKey: string) => {
    setMetadataMappings(prev => prev.map(mapping => 
      mapping.targetField === targetField 
        ? { ...mapping, sampleDataKey: sampleDataKey === '__none__' ? '' : sampleDataKey }
        : mapping
    ));
  };

  const addCustomMetadataField = () => {
    const newField: ExpectedMetadataField = {
      key: `custom_${Date.now()}`,
      label: 'Custom Field',
      type: 'text',
      required: false,
      description: 'Custom metadata field'
    };
    
    setCustomMetadataFields(prev => [...prev, newField]);
    
    // Add corresponding mapping
    setMetadataMappings(prev => [...prev, {
      sampleDataKey: '',
      targetField: newField.key,
      isRequired: false
    }]);
  };

  const updateCustomMetadataField = (index: number, updates: Partial<ExpectedMetadataField>) => {
    setCustomMetadataFields(prev => prev.map((field, i) => 
      i === index ? { ...field, ...updates } : field
    ));
  };

  const removeCustomMetadataField = (index: number) => {
    const fieldToRemove = customMetadataFields[index];
    setCustomMetadataFields(prev => prev.filter((_, i) => i !== index));
    setMetadataMappings(prev => prev.filter(mapping => mapping.targetField !== fieldToRemove.key));
  };

  const validateMappings = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!configName.trim()) {
      errors.push('Configuration name is required');
    }

    // Validate feature data mappings
    const requiredFields = expectedFields.filter(f => f.required);
    const mappedRequiredFields = fieldMappings.filter(m => m.isRequired && m.csvColumn);

    if (mappedRequiredFields.length < requiredFields.length) {
      const missingFields = requiredFields.filter(rf => 
        !fieldMappings.find(m => m.targetField === rf.key && m.csvColumn)
      );
      errors.push(`Required feature fields not mapped: ${missingFields.map(f => f.label).join(', ')}`);
    }

    // Validate Actual Value OR Deviation requirement
    const actualMapping = fieldMappings.find(m => m.targetField === 'actual_value');
    const deviationMapping = fieldMappings.find(m => m.targetField === 'deviation');
    const hasActual = actualMapping && actualMapping.csvColumn;
    const hasDeviation = deviationMapping && deviationMapping.csvColumn;

    if (!hasActual && !hasDeviation) {
      errors.push('Either Actual Value OR Deviation must be mapped');
    } else if (hasActual && hasDeviation) {
      errors.push('Only one of Actual Value OR Deviation can be mapped, not both');
    }

    // Validate metadata mappings - only if sample data exists
    if (Object.keys(sampleData).length > 0) {
      const requiredMetadataFieldKeys = requiredMetadataFields.map(f => f.key);
      const mappedRequiredMetadata = metadataMappings.filter(m => 
        requiredMetadataFieldKeys.includes(m.targetField) && m.sampleDataKey
      );

      if (mappedRequiredMetadata.length < requiredMetadataFields.length) {
        const missingMetadata = requiredMetadataFields.filter(rf => 
          !metadataMappings.find(m => m.targetField === rf.key && m.sampleDataKey)
        );
        errors.push(`Required metadata fields not mapped: ${missingMetadata.map(f => f.label).join(', ')}`);
      }

      // Check for duplicate metadata mappings
      const usedMetadataKeys = metadataMappings.filter(m => m.sampleDataKey).map(m => m.sampleDataKey);
      const metadataDuplicates = usedMetadataKeys.filter((key, index) => usedMetadataKeys.indexOf(key) !== index);
      if (metadataDuplicates.length > 0) {
        errors.push(`Metadata fields mapped multiple times: ${[...new Set(metadataDuplicates)].join(', ')}`);
      }
    }

    // Check for duplicate feature column mappings
    const usedColumns = fieldMappings.filter(m => m.csvColumn).map(m => m.csvColumn);
    const duplicates = usedColumns.filter((col, index) => usedColumns.indexOf(col) !== index);
    if (duplicates.length > 0) {
      errors.push(`Feature columns mapped multiple times: ${[...new Set(duplicates)].join(', ')}`);
    }

    return { isValid: errors.length === 0, errors };
  };

  const handleSave = () => {
    const validation = validateMappings();
    
    if (!validation.isValid) {
      toast({
        title: "Validation Failed",
        description: validation.errors.join('. '),
        variant: "destructive",
      });
      return;
    }

    const config = {
      name: configName,
      description: configDescription,
      fileType: file.name.split('.').pop()?.toLowerCase() || 'csv',
      delimiter,
      hasHeader,
      headerRowLabel: hasHeader ? headerRowLabel : null,
      sampleDataFields: Object.keys(sampleData),
      fieldMappings: fieldMappings.filter(m => m.csvColumn),
      metadataMappings: metadataMappings.filter(m => m.sampleDataKey),
      customMetadataFields,
      parsedColumns,
      expectedFields,
      expectedMetadataFields: allMetadataFields,
      dateFormat,
      timeFormat
    };

    onSave(config);
    toast({
      title: "Parsing Format Confirmed",
      description: `Parsing configuration "${configName}" has been created and selected.`,
    });
  };

  const handleClose = () => {
    setParsedColumns([]);
    setFieldMappings([]);
    setMetadataMappings([]);
    setCustomMetadataFields([]);
    setConfigName('');
    setConfigDescription('');
    setParsError('');
    setSampleData({});
    setHeaderRowLabel('Name');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Parse and Map Fields
          </DialogTitle>
          <DialogDescription>
            Configure how to parse and map columns from your CSV file to expected data fields.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Configuration Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Configuration Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="config-name">Configuration Name*</Label>
                  <Input
                    id="config-name"
                    value={configName}
                    onChange={(e) => setConfigName(e.target.value)}
                    placeholder="e.g., CMM Report Format"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="file-info">File</Label>
                  <Input value={file.name} disabled />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="config-description">Description</Label>
                <Textarea
                  id="config-description"
                  value={configDescription}
                  onChange={(e) => setConfigDescription(e.target.value)}
                  placeholder="Describe this parsing configuration..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Delimiter</Label>
                  <Select value={delimiter} onValueChange={setDelimiter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value=",">Comma (,)</SelectItem>
                      <SelectItem value=";">Semicolon (;)</SelectItem>
                      <SelectItem value="\t">Tab</SelectItem>
                      <SelectItem value="|">Pipe (|)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="has-header"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                  />
                  <Label htmlFor="has-header">File has meta data header</Label>
                </div>
              </div>
              {hasHeader && (
                <div className="space-y-2">
                  <Label htmlFor="header-label">Header Row Label</Label>
                  <Input
                    id="header-label"
                    value={headerRowLabel}
                    onChange={(e) => setHeaderRowLabel(e.target.value)}
                    placeholder="e.g., Name, Feature, ID"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the label that appears in the first column where your measurement data begins. Rows above this will be treated as metadata.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Parse Results */}
          {isLoading && (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-muted-foreground">Parsing file...</p>
              </CardContent>
            </Card>
          )}

          {parseError && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-red-800 font-medium">Parse Error</span>
                </div>
                <p className="text-red-700 text-sm mt-1">{parseError}</p>
              </CardContent>
            </Card>
          )}

          {!isLoading && !parseError && parsedColumns.length === 0 && (
            <Card className="border-gray-200 bg-gray-50">
              <CardContent className="p-6 text-center">
                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Configure your settings above and click "Parse File" to analyze the data structure.
                </p>
              </CardContent>
            </Card>
          )}

          {parsedColumns.length > 0 && (
            <>
              {/* Sample Data (Metadata) */}
              {Object.keys(sampleData).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Sample Data (Metadata)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(sampleData).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <span className="font-medium text-sm text-gray-700 min-w-0 flex-shrink-0">
                            {key}:
                          </span>
                          <span className="text-sm text-gray-900 truncate">
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Metadata Field Mapping */}
              {Object.keys(sampleData).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Metadata Field Mapping
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {allMetadataFields.map((field) => {
                        const mapping = metadataMappings.find(m => m.targetField === field.key);
                        const isCustomField = !requiredMetadataFields.find(rf => rf.key === field.key);
                        const customFieldIndex = customMetadataFields.findIndex(cf => cf.key === field.key);
                        
                        return (
                          <div key={field.key} className="flex items-center gap-4 p-3 border rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {isCustomField ? (
                                  <Input
                                    value={field.label}
                                    onChange={(e) => updateCustomMetadataField(customFieldIndex, { label: e.target.value })}
                                    className="font-medium text-sm w-48"
                                    placeholder="Field name"
                                  />
                                ) : (
                                  <span className="font-medium">{field.label}</span>
                                )}
                                {field.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                                {isCustomField && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeCustomMetadataField(customFieldIndex)}
                                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                              {field.description && (
                                <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                              )}
                            </div>
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                            <div className="w-64 flex flex-col gap-2">
                              <Select
                                value={mapping?.sampleDataKey || '__none__'}
                                onValueChange={(value) => updateMetadataMapping(field.key, value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select metadata..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">None</SelectItem>
                                  {Object.keys(sampleData).map((key) => (
                                    <SelectItem key={key} value={key}>
                                      <div className="flex items-center gap-2">
                                        <span>{key}</span>
                                        <span className="text-xs text-muted-foreground">
                                          ({sampleData[key]})
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {/* Inline radio buttons for Date and Time */}
                              {field.key === 'date' && (
                                <div className="mt-2">
                                  <Select value={dateFormat} onValueChange={setDateFormat}>
                                    <SelectTrigger className="w-48">
                                      <SelectValue placeholder="Select date format..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {DATE_FORMATS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              {field.key === 'time' && (
                                <div className="mt-2">
                                  <Select value={timeFormat} onValueChange={setTimeFormat}>
                                    <SelectTrigger className="w-48">
                                      <SelectValue placeholder="Select time format..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {TIME_FORMATS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Add Custom Field Button */}
                      <Button
                        variant="outline"
                        onClick={addCustomMetadataField}
                        className="w-full gap-2 mt-4"
                      >
                        <Plus className="w-4 h-4" />
                        Add Custom Metadata Field
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Detected Columns */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Table className="w-4 h-4" />
                    Detected Columns ({parsedColumns.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {parsedColumns.map((column) => (
                      <Card key={column.index} className="p-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{column.name}</span>
                            <Badge variant={
                              column.detectedType === 'number' ? 'default' :
                              column.detectedType === 'date' ? 'secondary' :
                              column.detectedType === 'mixed' ? 'destructive' : 'outline'
                            }>
                              {column.detectedType}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <div className="font-medium mb-1">Sample values:</div>
                            {column.sampleValues.slice(0, 3).map((value, idx) => (
                              <div key={idx} className="truncate">{value || '(empty)'}</div>
                            ))}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Field Mapping */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Feature Data Mapping
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {expectedFields.map((field) => {
                      const mapping = fieldMappings.find(m => m.targetField === field.key);
                      
                      // Check if this field should be disabled due to OR logic
                      const actualMapping = fieldMappings.find(m => m.targetField === 'actual_value');
                      const deviationMapping = fieldMappings.find(m => m.targetField === 'deviation');
                      const hasActual = actualMapping && actualMapping.csvColumn;
                      const hasDeviation = deviationMapping && deviationMapping.csvColumn;
                      
                      const isDisabled = 
                        (field.key === 'actual_value' && hasDeviation) ||
                        (field.key === 'deviation' && hasActual);
                      
                      return (
                        <div key={field.key} className={`flex items-center gap-4 p-3 border rounded-lg ${isDisabled ? 'opacity-50 bg-gray-50' : ''}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{field.label}</span>
                              {field.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                              {(field.key === 'actual_value' || field.key === 'deviation') && (
                                <Badge variant="outline" className="text-xs">OR</Badge>
                              )}
                            </div>
                            {field.description && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {field.description}
                                {(field.key === 'actual_value' || field.key === 'deviation') && 
                                  ' (Either Actual Value OR Deviation required)'
                                }
                              </p>
                            )}
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <div className="w-64">
                            <Select
                              value={mapping?.csvColumn || '__none__'}
                              onValueChange={(value) => updateFieldMapping(field.key, value)}
                              disabled={isDisabled}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select column..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {parsedColumns.map((col) => (
                                  <SelectItem key={col.index} value={col.name}>
                                    <div className="flex items-center gap-2">
                                      <span>{col.name}</span>
                                      <Badge variant="outline" className="text-xs">
                                        {col.detectedType}
                                      </Badge>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Parse Button */}
          {parsedColumns.length === 0 && !isLoading && (
            <div className="flex justify-center">
              <Button 
                onClick={parseFile}
                disabled={isLoading}
                className="gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Parsing...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Parse File
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={parsedColumns.length === 0 || !configName.trim() || !validateMappings().isValid}
            >
              <Save className="w-4 h-4 mr-2" />
              Confirm Parsing Format
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ParseMapModal; 